import { useState, useEffect, useCallback, useMemo } from 'react';
// Note: Admin materials can include dynamic types (e.g., 'artcard') that
// are not part of the customer-facing Material union. Use string here.
import { fetchWithInterceptors } from '../utils/apiInterceptors';

export interface MaterialGsmData {
  [gsm: string]: number;
}

export interface MaterialData {
  displayName: string;
  gsmValues: MaterialGsmData;
  gsmOptions: {
    [gsm: string]: {
      pricePerKg: number;
    };
  };
}

export interface MaterialsResponse {
  success: boolean;
  data: {
    [materialType: string]: MaterialData;
  };
  metadata?: {
    version: string;
    lastUpdated: string;
  };
}

export interface MaterialApiError {
  success: false;
  error: string;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export const useMaterials = () => {
  const [materials, setMaterials] = useState<{ [key: string]: MaterialData }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Memoize to prevent infinite loop in StrictMode
  const fetchMaterials = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetchWithInterceptors(`${API_BASE_URL}/api/admin/materials`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        // Handle authentication errors specifically
        if (response.status === 401) {
          throw new Error('Authentication required. Please sign in again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: MaterialsResponse = await response.json();

      if (data.success && data.data) {
        // Validate and sanitize the data before setting
        const sanitizedData: { [key: string]: MaterialData } = {};
        Object.entries(data.data).forEach(([key, value]) => {
          if (value && typeof value === 'object' && value.gsmValues) {
            sanitizedData[key] = value;
          }
        });
        setMaterials(sanitizedData);
      } else {
        throw new Error('Failed to fetch materials');
      }

    } catch (err) {
      console.error('Error fetching materials:', err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
      // Don't set empty materials on error to prevent re-render issues
      if (err instanceof Error && err.message.includes('Authentication')) {
        // Keep existing materials data if it's just an auth error
        // This prevents the component from crashing
      } else {
        setMaterials({});
      }
    } finally {
      setLoading(false);
    }
  }, []); // Empty deps - stable function

  const getMaterial = useCallback(async (materialType: string): Promise<MaterialData | null> => {
    try {
      const response = await fetchWithInterceptors(`${API_BASE_URL}/api/admin/materials/${materialType}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.success ? data.data : null;

    } catch (err) {
      console.error('Error fetching material:', err);
      return null;
    }
  }, []);

  const updateMaterialGsm = useCallback(async (
    materialType: string, 
    gsm: string, 
    pricePerKg: number
  ): Promise<boolean> => {
    try {
      const response = await fetchWithInterceptors(`${API_BASE_URL}/api/admin/materials/${materialType}/gsm`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          gsm,
          pricePerKg
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        // Update local state
        setMaterials(prev => ({
          ...prev,
          [materialType]: {
            ...prev[materialType],
            gsmValues: {
              ...prev[materialType]?.gsmValues,
              [gsm]: pricePerKg
            },
            gsmOptions: {
              ...prev[materialType]?.gsmOptions,
              [gsm]: { pricePerKg }
            }
          }
        }));
        return true;
      }
      
      return false;

    } catch (err) {
      console.error('Error updating material GSM:', err);
      setError(err instanceof Error ? err.message : 'Failed to update GSM');
      return false;
    }
  }, []);

  const removeMaterialGsm = useCallback(async (
    materialType: string, 
    gsm: string
  ): Promise<boolean> => {
    try {
      const response = await fetchWithInterceptors(`${API_BASE_URL}/api/admin/materials/${materialType}/gsm/${gsm}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        // Update local state
        setMaterials(prev => {
          const updatedMaterial = { ...prev[materialType] };
          delete updatedMaterial.gsmValues[gsm];
          delete updatedMaterial.gsmOptions[gsm];
          
          return {
            ...prev,
            [materialType]: updatedMaterial
          };
        });
        return true;
      }
      
      return false;

    } catch (err) {
      console.error('Error removing material GSM:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove GSM');
      return false;
    }
  }, []);

  const addMaterial = useCallback(async (
    materialType: string,
    displayName: string,
    defaultGsm: number,
    defaultPrice: number
  ): Promise<boolean> => {
    try {
      const response = await fetchWithInterceptors(`${API_BASE_URL}/api/admin/materials`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          materialType,
          displayName,
          defaultGsm,
          defaultPrice
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        // Refresh materials list
        await fetchMaterials();
        return true;
      }
      
      return false;

    } catch (err) {
      console.error('Error adding material:', err);
      setError(err instanceof Error ? err.message : 'Failed to add material');
      return false;
    }
  }, [fetchMaterials]);

  // Memoize clearError to prevent recreation
  const clearError = useCallback(() => setError(null), []);

  // Fetch materials on mount
  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]); // Safe - fetchMaterials is memoized

  // Memoize the return object to prevent infinite re-renders
  // The object will only be recreated when any of its dependencies change
  return useMemo(() => ({
    materials,
    loading,
    error,
    fetchMaterials,
    getMaterial,
    updateMaterialGsm,
    removeMaterialGsm,
    addMaterial,
    clearError
  }), [materials, loading, error, fetchMaterials, getMaterial, 
       updateMaterialGsm, removeMaterialGsm, addMaterial, clearError]);
};
